import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, Link, useLocation, Navigate, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Target, 
  CreditCard, 
  TrendingUp, 
  Trophy,
  Bell,
  Bot,
  MapPin,
  Menu,
  X,
  Settings,
  ChevronDown,
  Wallet,
  CircleDollarSign,
  LineChart,
  Sparkles,
  AlertTriangle,
  TrendingDown,
  DollarSign,
  Calendar,
  Search,
  Plus,
  ArrowLeft
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import NotificationSystem from "../components/NotificationSystem";

// Import dashboard components
import Overview from './Dashboard/Overview';
import Budget from './Dashboard/Budget';
import Subscriptions from './Dashboard/Subscriptions';
import Investments from './Dashboard/Investments';
import Challenges from './Dashboard/Challenges';
import AIAssistant from './Dashboard/AIAssistant';
import LocationInsights from './Dashboard/LocationInsights';
import Notifications from './Dashboard/Notifications';
import SpendingAnalytics from './Dashboard/SpendingAnalytics';
import BudgetCategories from './Dashboard/BudgetCategories';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Transaction {
  id: number;
  name: string;
  amount: number;
  date: string;
  type: 'income' | 'expense';
}

interface Bill {
  name: string;
  amount: number;
  dueDate: string;
}

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [balance, setBalance] = useState(12567.89);
  const [aiSuggestion, setAiSuggestion] = useState("💡 You could save $240 this month by optimizing your subscriptions");
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('week');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const [financialGoals] = useState([
    {
      name: 'Emergency Fund',
      current: 8000,
      target: 10000,
      progress: 80
    },
    {
      name: 'New Car',
      current: 15000,
      target: 30000,
      progress: 50
    },
    {
      name: 'Vacation',
      current: 2000,
      target: 5000,
      progress: 40
    }
  ]);

  const [recentAlerts] = useState([
    {
      id: 1,
      message: 'Large transaction detected: $500 at Electronics Store',
      timestamp: '2 hours ago',
      type: 'transaction'
    },
    {
      id: 2,
      message: 'TSLA is down 5% - good buying opportunity',
      timestamp: '4 hours ago',
      type: 'investment'
    }
  ]);

  const [balanceData] = useState({
    current: 24450.00,
    change: '+2.5%',
    monthlyData: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      values: [2400, 1400, 3200, 2800, 1800, 2400]
    }
  });

  const [stockInsights] = useState([
    {
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      change: -5.2,
      recommendation: 'Consider buying on this dip'
    },
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      change: 2.1,
      recommendation: 'Strong fundamentals, hold position'
    },
    {
      symbol: 'NVDA',
      name: 'NVIDIA Corp.',
      change: 4.5,
      recommendation: 'AI growth potential remains strong'
    }
  ]);

  const mockData = {
    balance: 4785.85,
    expenses: 3200,
    income: 6500,
    upcomingBills: [
      { name: "Credit Card", amount: 150, dueDate: "March 28" },
      { name: "Netflix", amount: 15.99, dueDate: "March 30" },
      { name: "Utilities", amount: 85, dueDate: "April 1" }
    ] as Bill[],
    recentTransactions: [
      { id: 1, name: "Amazon", amount: 45.99, date: "Today", type: 'expense' },
      { id: 2, name: "Salary Deposit", amount: 2500, date: "Yesterday", type: 'income' },
      { id: 3, name: "Uber", amount: 24.50, date: "Yesterday", type: 'expense' },
      { id: 4, name: "Grocery Store", amount: 85.75, date: "Mar 20", type: 'expense' }
    ] as Transaction[]
  };

  const sidebarItems = [
    {
      path: '',
      label: 'Dashboard Home',
      icon: <LayoutDashboard size={20} />,
      description: 'Financial overview and quick actions'
    },
    {
      path: 'budget',
      label: 'Budget Tracker',
      icon: <Target size={20} />,
      description: 'Track and manage your spending',
      subItems: [
        { path: 'analytics', label: 'Spending Analytics', icon: <LineChart size={16} /> },
        { path: 'categories', label: 'Budget Categories', icon: <CircleDollarSign size={16} /> }
      ]
    },
    {
      path: 'subscriptions',
      label: 'Subscription Manager',
      icon: <CreditCard size={20} />,
      description: 'Manage recurring payments'
    },
    {
      path: 'investments',
      label: 'Investments & Stocks',
      icon: <TrendingUp size={20} />,
      description: 'Portfolio and market insights',
      subItems: [
        { path: 'portfolio', label: 'My Portfolio' },
        { path: 'watchlist', label: 'Stock Watchlist' }
      ]
    },
    {
      path: 'challenges',
      label: 'Financial Goals',
      icon: <Trophy size={20} />,
      description: 'Set and track financial goals'
    },
    {
      path: 'ai-assistant',
      label: 'AI Assistant',
      icon: <Bot size={20} />,
      description: 'Get personalized guidance'
    },
    {
      path: 'location',
      label: 'Location Insights',
      icon: <MapPin size={20} />,
      description: 'Spending patterns by location'
    },
    {
      path: 'notifications',
      label: 'Notifications',
      icon: <Bell size={20} />,
      description: 'Important updates and alerts'
    }
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        grid: {
          display: false
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const chartData = {
    labels: balanceData.monthlyData.labels,
    datasets: [
      {
        data: balanceData.monthlyData.values,
        borderColor: '#68A511',
        backgroundColor: 'rgba(104, 165, 17, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  useEffect(() => {
    // Show notifications after a short delay
    const timer = setTimeout(() => {
      setShowNotifications(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const filterTransactions = () => {
    return mockData.recentTransactions.filter(transaction => {
      const matchesSearch = transaction.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTab = activeTab === 'all' || transaction.type === activeTab;
      return matchesSearch && matchesTab;
    });
  };

  const handleSidebarItemClick = (path: string) => {
    if (path === '') {
      navigate('/dashboard');
    } else if (path === 'budget') {
      navigate('/dashboard/budget');
    } else if (path === 'ai-assistant') {
      navigate('/dashboard/ai-assistant');
    } else if (path === 'location') {
      navigate('/dashboard/location');
    } else {
      navigate(`/dashboard/${path}`);
    }
    
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-[#EEF4E1] to-white dark:from-gray-900 dark:to-gray-800 pt-16">
      <NotificationSystem 
        show={showNotifications}
        balance={mockData.balance}
        expenses={mockData.expenses}
        upcomingBills={mockData.upcomingBills}
      />
      {/* Top Bar */}
      <div className="fixed top-16 right-0 left-0 z-40 h-16 bg-white/30 dark:bg-gray-800/30 backdrop-blur-md border-b border-gray-200/20">
        <div className={`flex items-center justify-between h-full px-6 ${isSidebarOpen ? 'lg:ml-64' : ''}`}>
          <div className="flex items-center gap-6">
            {/* Navigation Arrows */}
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="p-2 rounded-xl hover:bg-[#DCE9C2] text-[#68A511] transition-all duration-300 flex items-center gap-2"
              >
                <ArrowLeft size={20} />
                <span className="text-sm">Home</span>
              </Link>
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl hover:bg-[#DCE9C2] text-[#68A511] transition-all duration-300"
              >
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="h-8 w-px bg-gray-200/50 mx-2" />
            <div className="flex items-center gap-4">
              <CircleDollarSign className="text-[#68A511]" size={24} />
              <span className="text-xl font-semibold">${mockData.balance.toLocaleString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="hidden md:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
            >
              <Sparkles className="text-[#68A511]" size={16} />
              {aiSuggestion}
            </motion.div>
            <button className="p-2 rounded-full bg-[#DCE9C2] hover:bg-[#68A511] text-[#598615] hover:text-white transition-all duration-300">
              <Bell size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-20 left-4 z-50 lg:hidden bg-white/50 dark:bg-gray-800/50 backdrop-blur-md p-2 rounded-lg shadow-lg hover:bg-white/60 transition-all duration-300"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <AnimatePresence mode="wait">
        {isSidebarOpen && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white/30 dark:bg-gray-800/30 backdrop-blur-md shadow-xl z-40 overflow-y-auto scrollbar-thin scrollbar-thumb-[#68A511] scrollbar-track-transparent"
          >
            <div className="p-6 space-y-6">
              {sidebarItems.map((item) => (
                <div key={item.path}>
                  <Link
                    to={item.path === '' ? '/dashboard' : `/dashboard/${item.path}`}
                    className="group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-[#DCE9C2] hover:shadow-lg"
                    onClick={(e) => {
                      if (item.subItems) {
                        e.preventDefault();
                        setExpandedSection(expandedSection === item.path ? null : item.path);
                      } else {
                        handleSidebarItemClick(item.path);
                      }
                    }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="p-2 rounded-lg bg-white/50 dark:bg-gray-700/50 group-hover:bg-[#68A511] group-hover:text-white text-[#68A511]"
                    >
                      {item.icon}
                    </motion.div>
                    <div>
                      <div className="font-medium text-gray-700 dark:text-gray-200 group-hover:text-[#598615]">
                        {item.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {item.description}
                      </div>
                    </div>
                    {item.subItems && (
                      <ChevronDown
                        size={16}
                        className={`ml-auto transform transition-transform ${
                          expandedSection === item.path ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </Link>
                  
                  {item.subItems && expandedSection === item.path && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-12 mt-2 space-y-1"
                    >
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={`/dashboard/${item.path}/${subItem.path}`}
                          className="block px-3 py-2 text-sm rounded-lg text-gray-600 hover:text-[#598615] hover:bg-[#DCE9C2] transition-colors"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div 
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? 'lg:ml-64' : ''
        } mt-16`}
      >
        <div className="flex-1 overflow-y-auto p-8">
          <Routes>
            <Route index element={
              <div>
                {/* Quick Actions */}
                <div className="flex items-center justify-between mb-8 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-[#68A511] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#527f0e] transition-colors"
                  >
                    <Plus size={20} />
                    Add Transaction
                  </motion.button>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#68A511]"
                    />
                  </div>
                </div>

                {/* Balance and Stock Insights Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  {/* Balance Card */}
                  <div className="lg:col-span-2 bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h2 className="text-gray-600">Total Balance</h2>
                        <div className="flex items-center gap-4">
                          <p className="text-4xl font-bold text-gray-800">${mockData.balance.toLocaleString()}</p>
                          <span className="px-2 py-1 bg-[#68A511] text-white rounded-lg text-sm">
                            {balanceData.change}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="h-[200px]">
                      <Line options={chartOptions} data={chartData} />
                    </div>

                    <div className="flex gap-4 mt-6">
                      <button className="flex-1 py-3 px-4 rounded-xl bg-[#DCE9C2] text-[#68A511] hover:bg-[#68A511] hover:text-white transition-all duration-300">
                        Quick Transfer
                      </button>
                      <button className="flex-1 py-3 px-4 rounded-xl bg-[#DCE9C2] text-[#68A511] hover:bg-[#68A511] hover:text-white transition-all duration-300">
                        View Analytics
                      </button>
                    </div>
                  </div>

                  {/* Stock Insights Card */}
                  <div className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-[#68A511] mb-4">Stock Insights</h2>
                    <div className="space-y-4">
                      {stockInsights.map((stock) => (
                        <div key={stock.symbol} className="p-4 bg-[#DCE9C2]/50 rounded-xl">
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <span className="font-bold text-gray-800">{stock.symbol}</span>
                              <span className="text-sm text-gray-600 ml-2">{stock.name}</span>
                            </div>
                            <span className={`${
                              stock.change >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {stock.change >= 0 ? '+' : ''}{stock.change}%
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{stock.recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Financial Goals Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8"
                >
                  <h2 className="text-xl font-bold text-[#68A511] mb-4">Financial Goals</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {financialGoals.map((goal, index) => (
                      <div key={index} className="bg-white/30 backdrop-blur-md rounded-xl p-4 shadow-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-700">{goal.name}</span>
                          <span className="text-sm text-gray-500">
                            ${goal.current.toLocaleString()} / ${goal.target.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-[#68A511] rounded-full transition-all duration-500"
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Recent Alerts Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8"
                >
                  <h2 className="text-xl font-bold text-[#68A511] mb-4">Recent Alerts</h2>
                  <div className="space-y-4">
                    {recentAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="bg-white/30 backdrop-blur-md rounded-xl p-4 shadow-lg flex items-start gap-3"
                      >
                        <div className="p-2 rounded-full bg-[#DCE9C2]">
                          <AlertTriangle className="text-[#68A511]" size={20} />
                        </div>
                        <div>
                          <p className="text-gray-700">{alert.message}</p>
                          <span className="text-sm text-gray-500">{alert.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Transactions */}
                <div className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">Recent Transactions</h2>
                    <select 
                      className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-1 text-sm"
                      value={selectedTimeframe}
                      onChange={(e) => setSelectedTimeframe(e.target.value)}
                    >
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="year">This Year</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    {mockData.recentTransactions.map((transaction) => (
                      <motion.div 
                        key={transaction.id}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                        whileHover={{ scale: 1.01 }}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            transaction.type === 'income' ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'
                          }`}>
                            {transaction.type === 'income' ? 
                              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" /> : 
                              <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                            }
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 dark:text-white">{transaction.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{transaction.date}</p>
                          </div>
                        </div>
                        <p className={`font-medium ${
                          transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}${transaction.amount}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Bills */}
                <div className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Upcoming Bills</h2>
                  <div className="space-y-4">
                    {mockData.upcomingBills.map((bill, index) => (
                      <motion.div 
                        key={index}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                        whileHover={{ scale: 1.01 }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 dark:text-white">{bill.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Due {bill.dueDate}</p>
                          </div>
                        </div>
                        <p className="font-medium text-gray-800 dark:text-white">${bill.amount}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            } />
            <Route path="budget" element={<Budget />} />
            <Route path="budget/analytics" element={<SpendingAnalytics />} />
            <Route path="budget/categories" element={<BudgetCategories />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="investments" element={<Investments />} />
            <Route path="challenges" element={<Challenges />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="location" element={<LocationInsights />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 